/**
 * Workaround for a PlayCanvas WebGPU-only splat artefact: radial, rainbow-coloured streaks
 * across the sky of outdoor scans (Chimney Bluff), worst at device pixel ratio 2. WebGL2 and
 * WebGPU with CPU sorting draw the same view cleanly.
 *
 * Cause (engine 2.23.x, unchanged on engine main as of 2026-10-18): on WebGPU the viewer uses
 * the GPU-sort renderer, which projects splats in a compute shader (`GSplatProjector*`). When a
 * splat's projected radius is over the 1024 px cap, `computeSplatCov` divides the whole 2D
 * covariance by `capScale²`. The projector then caps each eigen-axis at 1024 px and the vertex
 * shader renormalises the quad, which is what the quad renderers (`gsplatCorner`) do. The
 * extra uniform rescale shrinks the minor axis as well, so large elongated splats (the scan's
 * sky and background splats) come out as thin, hard needles instead of soft smears. The needles
 * line up on a vanishing point and show the splats' different colours side by side, which is
 * what looks like a rainbow streak. At DPR 2 every splat covers twice as many pixels while the
 * cap stays at 1024 px, so more of them go over it (about 200 instead of 55 at the Chimney Bluff
 * pose, by an offline estimate).
 *
 * Fix: set `capScale` to 1 in the projector. Each axis is then capped like the quad renderers
 * do it, and oversized splats render the same as on WebGL2. Only the 2D covariance handed to
 * the eigen-decomposition changes; the culling tests above it are untouched. The patch rewrites
 * the preprocessed compute source just before the device compiles it. If a future engine
 * changes that line (for example an upstream fix), the pattern stops matching and the engine's
 * shader runs unmodified. `gsplatProjectorCapFix.test.ts` fails in that case so the workaround
 * gets reviewed and removed.
 */

/** The engine line that applies the isotropic cap rescale (whitespace-tolerant). */
const CAP_SCALE_PATTERN = /let\s+capScale\s*=\s*max\(\s*1\.0\s*,\s*max\(\s*radiusXUncapped\s*,\s*radiusYUncapped\s*\)\s*\/\s*maxRadius\s*\)\s*;/;

/** Replacement: no extra rescale; the projector's per-axis cap does the work. */
export const PATCHED_CAP_SCALE_LINE = 'let capScale = 1.0;';

/** Compute shaders built by PlayCanvas's GSplatProjector are named `GSplatProjector<Radial|Linear>...`. */
const PROJECTOR_SHADER_PREFIX = 'GSplatProjector';

const INSTALLED = Symbol.for('storysplat.gsplatProjectorCapFix');

/** True when the WGSL contains the engine's isotropic cap rescale line. */
export function hasIsotropicCapRescale(source: string): boolean {
    return CAP_SCALE_PATTERN.test(source);
}

/** Returns the WGSL with the isotropic cap rescale removed (unchanged if the line is absent). */
export function patchGsplatProjectorSource(source: string): string {
    return source.replace(CAP_SCALE_PATTERN, PATCHED_CAP_SCALE_LINE);
}

interface ShaderLike {
    name?: unknown;
    definition?: { cshader?: unknown } | null;
}

interface DeviceLike {
    isWebGPU?: boolean;
    createShaderImpl?: (shader: ShaderLike) => unknown;
    [INSTALLED]?: boolean;
}

/**
 * Patches the GSplat projector compute shaders compiled on this device. Call once, right after
 * the WebGPU device is created and before any splat renders. No-op on WebGL (the bug is in the
 * WebGPU-only projector) and when called again on the same device.
 *
 * @returns true when the hook is installed on this device.
 */
export function installGsplatProjectorCapFix(device: unknown): boolean {
    const gpu = device as DeviceLike | null | undefined;
    if (!gpu || gpu.isWebGPU !== true) return false;
    if (gpu[INSTALLED]) return true;
    const createShaderImpl = gpu.createShaderImpl;
    if (typeof createShaderImpl !== 'function') return false;

    let reported = false;
    gpu.createShaderImpl = function patchedCreateShaderImpl(this: DeviceLike, shader: ShaderLike) {
        const definition = shader?.definition;
        if (definition && typeof definition.cshader === 'string'
            && typeof shader.name === 'string' && shader.name.startsWith(PROJECTOR_SHADER_PREFIX)) {
            const patched = patchGsplatProjectorSource(definition.cshader);
            if (!reported) {
                reported = true;
                if (patched !== definition.cshader) {
                    console.log('[SPLAT] WebGPU GSplat projector: per-axis radius cap applied (oversized splats match WebGL2)');
                } else {
                    console.info('[SPLAT] WebGPU GSplat projector: cap rescale line not found; engine shader runs unmodified');
                }
            }
            definition.cshader = patched;
        }
        return createShaderImpl.call(this, shader);
    };
    gpu[INSTALLED] = true;
    return true;
}
